'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  createColumnHelper, getCoreRowModel, useReactTable
} from '@tanstack/react-table';
import clsx from 'clsx';
import React, {
  useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/button/button';
import { Input } from '@/components/input/input';
import { Loader } from '@/components/loader/loader';
import Modal from '@/components/modal/modal';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { CursorPaginationState } from '@/components/pagination/pagination';
import { buildColumnClassString, ColumnModEnums } from '@/components/table/table';
import { Heading } from '@/components/typography/heading';
import { useMetricsSearch } from '@/hooks/queries/useMetrics';
import { useCursorPagination } from '@/hooks/useCursorPagination';
import { useModalKeyboard } from '@/hooks/useModalKeyboard';
import {
  EvalCreateReq,
  MetricConfig
} from '@/types/evals';
import { getMetricType } from '@/utils/metricUtils';

import { MetricPeekButton } from './metricPeekButton';
import styles from './modalEvaluationCreate.module.scss';

const NAME_MAX = 50;
const DESCRIPTION_MAX = 150;
const PAGE_SIZE = 10;

const formSchema = z.object( {
  name: z.string().trim().min( 1, 'Name is required' ).max( NAME_MAX, `Max ${ NAME_MAX } characters` ),
  description: z.string().max( DESCRIPTION_MAX, `Max ${ DESCRIPTION_MAX } characters` ).optional()
} );

type FormData = z.infer<typeof formSchema>;

type Props = {
  cb: ( ret: EvalCreateReq ) => void;
  onClose: () => void;
  isPending?: boolean;
  isSuccess?: boolean;
};

const columnHelper = createColumnHelper<MetricConfig>();

type MetricsTableProps = {
  metrics: MetricConfig[];
  selectedMetrics: Map<string, MetricConfig>;
  selectedVersions: Map<string, number>;
  isPending?: boolean;
  cursorPagination: CursorPaginationState;
  onMetricToggle: ( metric: MetricConfig, version: number, checked: boolean ) => void;
  onMetricVersionChange: ( metric: MetricConfig, version: number ) => void;
};

function MetricsTable( {
  metrics,
  selectedMetrics,
  selectedVersions,
  isPending,
  cursorPagination,
  onMetricToggle,
  onMetricVersionChange
}: MetricsTableProps ) {
  const columns = useMemo( () => [
    columnHelper.display( {
      id: 'select',
      header: '',
      size: 50,
      cell: info => {
        const metric = info.row.original;
        const latestVersion = metric.latestVersionNumber;
        const selectedVersion = selectedVersions.get( metric.metricProviderId ) ?? latestVersion;
        return (
          <input
            checked={ selectedMetrics.has( metric.metricProviderId ) }
            onChange={ ( e ) => onMetricToggle( metric, selectedVersion, e.target.checked ) }
            type='checkbox'
            disabled={ isPending }
            data-testid={ `metric-checkbox-${ metric.metricProviderId }` }
          />
        );
      },
      meta: {
        classStr: buildColumnClassString( { width: 5, styles: ColumnModEnums.CENTER } )
      }
    } ),
    columnHelper.accessor( 'name', {
      header: 'Metric Name',
      cell: info => {
        const metric = info.row.original;
        const latestVersion = metric.latestVersionNumber;
        const selectedVersion = selectedVersions.get( metric.metricProviderId ) ?? latestVersion;
        return (
          <div className={ styles.metric_name_cell } title={ info.getValue() }>
            <MetricPeekButton
              metricId={ metric.metricProviderId }
              metricName={ metric.name }
              version={ selectedVersion }
            />
            <span>{ info.getValue() }</span>
          </div>
        );
      },
      meta: {
        classStr: buildColumnClassString( { width: 35, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.display( {
      id: 'version',
      header: 'Version',
      cell: info => {
        const metric = info.row.original;
        const latestVersion = metric.latestVersionNumber;
        const selectedVersion = selectedVersions.get( metric.metricProviderId ) ?? latestVersion;
        return (
          <select
            value={ selectedVersion }
            onChange={ ( e ) => onMetricVersionChange( metric, parseInt( e.target.value ) ) }
            disabled={ isPending }
          >
            { Array.from( { length: metric.latestVersionNumber }, ( _, i ) => i + 1 ).map( v => (
              <option value={ v } key={ `metric-${ metric.metricProviderId }-vopt-${ v }` }>{ v }</option>
            ) ) }
          </select>
        );
      },
      meta: {
        classStr: buildColumnClassString( { width: 10, styles: ColumnModEnums.CENTER } )
      }
    } ),
    columnHelper.display( {
      id: 'access',
      header: 'Your Access',
      cell: () => 'Owner',
      meta: {
        classStr: buildColumnClassString( { width: 15 } )
      }
    } ),
    columnHelper.accessor( ( row ) => getMetricType( row.configuration ), {
      id: 'metricType',
      header: 'Metric Type',
      cell: info => info.getValue(),
      meta: {
        classStr: buildColumnClassString( { width: 15 } )
      }
    } ),
    columnHelper.accessor( 'description', {
      header: 'Description',
      cell: info => {
        const description = info.getValue() || '-';
        return <span title={ description }>{ description }</span>;
      },
      meta: {
        classStr: buildColumnClassString( { width: 20, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } )
  ], [ selectedMetrics, selectedVersions, isPending, onMetricToggle, onMetricVersionChange ] );

  const table = useReactTable( {
    data: metrics,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false
  } );

  return <NativeTable table={ table } cursorPagination={ cursorPagination } />;
}

export default function ModalEvaluationCreate( { cb, onClose, isPending, isSuccess }: Props ) {
  const [ selectedMetrics, setSelectedMetrics ] = useState<Map<string, MetricConfig>>( new Map() );
  const [ selectedVersions, setSelectedVersions ] = useState<Map<string, number>>( new Map() );
  const [ metricNameFilter, setMetricNameFilter ] = useState( '' );
  const { cursors, currentCursorIdx, currentCursor: cursor, currentPage, canGoPrev, goNext, goPrev, reset: resetCursor } = useCursorPagination();
  const [ pageSize, setPageSize ] = useState( PAGE_SIZE );
  const [ submitted, setSubmitted ] = useState<{ name: string; metricNames: string[] } | undefined>( undefined );

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<FormData>( {
    resolver: zodResolver( formSchema ),
    defaultValues: { name: '', description: '' },
    mode: 'onChange'
  } );

  useModalKeyboard( { onEscape: () => { if ( !isPending ) onClose(); } } );

  const nameValue = watch( 'name' ) ?? '';
  const descriptionValue = watch( 'description' ) ?? '';

  const { data, isLoading, isError } = useMetricsSearch( {
    limit: pageSize,
    cursor: cursor ?? undefined,
    is_deprecated: false,
    name: metricNameFilter || undefined,
    sort_by: cursor ? undefined : 'created_at',
    sort_order: cursor ? undefined : 'desc'
  } );

  // Pinning is only recomputed when filter/page/data changes — not on selection
  // toggles, so rows don't jump around when the user clicks a checkbox.
  const selectedMetricsRef = useRef( selectedMetrics );
  useEffect( () => {
    selectedMetricsRef.current = selectedMetrics;
  }, [ selectedMetrics ] );

  const [ displayedMetrics, setDisplayedMetrics ] = useState<MetricConfig[]>( [] );

  useEffect( () => {
    const items = data?.items ?? [];
    const onPageOne = currentCursorIdx === 0;
    const filter = metricNameFilter.trim().toLowerCase();
    const selected = selectedMetricsRef.current;

    if ( !onPageOne ) {
      setDisplayedMetrics( items.filter( m => !selected.has( m.metricProviderId ) ) );
      return;
    }

    const pinned = Array.from( selected.values() ).filter( m => !filter || m.name.toLowerCase().includes( filter )
    );
    const rest = items.filter( m => !selected.has( m.metricProviderId ) );
    setDisplayedMetrics( [ ...pinned, ...rest ] );
  }, [ data?.items, currentCursorIdx, metricNameFilter ] );

  const hasNoAccessibleMetrics = !isLoading && !isError && data !== undefined && data.items.length === 0 && !metricNameFilter && selectedMetrics.size === 0;

  const handleNextPage = useCallback( () => {
    if ( data?.nextCursor ) goNext( data.nextCursor );
  }, [ data?.nextCursor, goNext ] );

  const handlePageSizeChange = useCallback( ( size: number ) => {
    setPageSize( size );
    resetCursor();
  }, [ resetCursor ] );

  const onMetricToggle = useCallback( ( metric: MetricConfig, version: number, checked: boolean ) => {
    setSelectedMetrics( prev => {
      const next = new Map( prev );
      if ( checked ) next.set( metric.metricProviderId, metric );
      else next.delete( metric.metricProviderId );
      return next;
    } );
    if ( checked ) {
      setSelectedVersions( prev => {
        const next = new Map( prev );
        next.set( metric.metricProviderId, version );
        return next;
      } );
    }
  }, [] );

  const onMetricVersionChange = useCallback( ( metric: MetricConfig, version: number ) => {
    setSelectedVersions( prev => {
      const next = new Map( prev );
      next.set( metric.metricProviderId, version );
      return next;
    } );
    setSelectedMetrics( prev => {
      if ( prev.has( metric.metricProviderId ) ) return prev;
      const next = new Map( prev );
      next.set( metric.metricProviderId, metric );
      return next;
    } );
  }, [] );

  const onSubmit = ( formData: FormData ) => {
    if ( selectedMetrics.size === 0 ) return;

    const name = formData.name.trim();
    const req: EvalCreateReq = {
      name,
      description: formData.description?.trim() || undefined,
      metricProviders: Array.from( selectedMetrics.entries() ).map( ( [ id, metric ] ) => {
        const version = selectedVersions.get( id ) ?? metric.latestVersionNumber;
        return { metricProviderId: id, version: version.toString() };
      } )
    };

    setSubmitted( {
      name,
      metricNames: Array.from( selectedMetrics.values() ).map( m => m.name )
    } );
    cb( req );
  };

  const nameRemaining = NAME_MAX - nameValue.length;
  const descRemaining = DESCRIPTION_MAX - descriptionValue.length;
  const canSubmit = !isPending && !!nameValue.trim() && selectedMetrics.size > 0 && !errors.name && !errors.description;

  if ( isSuccess && submitted ) {
    return (
      <Modal
        className={ styles.modal }
        resizable
        modalType="evaluation-create"
        initialSize="large"
        minSize={ { width: 720, height: 600 } }
      >
        <div className={ styles.form } data-testid="evaluation-create-success">
          <div className={ styles.header }>
            <Heading as="h2" size="md">Evaluation Set Created</Heading>
            <span className={ styles.subheader }>
              Your evaluation set named &ldquo;<strong>{ submitted.name }</strong>&rdquo; was successfully created with the following { submitted.metricNames.length } metric{ submitted.metricNames.length === 1 ? '' : 's' }.
            </span>
          </div>
          <ul className={ styles.success_list }>
            { submitted.metricNames.map( ( name, idx ) => (
              <li key={ `${ name }-${ idx }` }>{ name }</li>
            ) ) }
          </ul>
          <div className={ styles.footer }>
            <Button variant="secondary" size="sm" type="button" onClick={ onClose } data-testid="evaluation-success-close-button">
              Close
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      className={ styles.modal }
      resizable
      modalType="evaluation-create"
      initialSize="large"
      minSize={ { width: 720, height: 600 } }
    >
      <form className={ styles.form } onSubmit={ handleSubmit( onSubmit ) }>
        <div className={ styles.header }>
          <Heading as="h2" size="md">Create New Evaluation Set</Heading>
          <span className={ styles.subheader }>
            Run metrics you are an Owner of on real agent interactions to measure agentic system performance.
          </span>
        </div>

        <div className={ styles.field }>
          <div className={ styles.field_header }>
            <label htmlFor="evaluation-name" className={ clsx( styles.field_label, errors.name && styles.required ) }>
              Evaluation Name
            </label>
            <span className={ clsx( styles.char_count, nameRemaining < 0 && styles.char_count_error ) }>
              { nameValue.length === 0 ? `Max ${ NAME_MAX } characters` : `${ nameRemaining } characters remaining` }
            </span>
          </div>
          <Input
            id="evaluation-name"
            type="text"
            placeholder='Ex. "Helpfulness and Humor Evaluation for my Agent"'
            disabled={ isPending }
            data-testid="evaluation-name-input"
            error={ errors.name?.message }
            { ...register( 'name' ) }
          />
        </div>

        <div className={ styles.field }>
          <div className={ styles.field_header }>
            <label htmlFor="evaluation-description" className={ clsx( styles.field_label, errors.description && styles.required ) }>
              Description (optional)
            </label>
            <span className={ clsx( styles.char_count, descRemaining < 0 && styles.char_count_error ) }>
              { descriptionValue.length === 0 ? `Max ${ DESCRIPTION_MAX } characters` : `${ descRemaining } characters remaining` }
            </span>
          </div>
          <textarea
            id="evaluation-description"
            className={ clsx( styles.textarea_input, errors.description && styles.input_error ) }
            placeholder='Ex. "Evaluates the helpfulness and humor of my agent"'
            rows={ 2 }
            disabled={ isPending }
            data-testid="evaluation-description-input"
            { ...register( 'description' ) }
          />
          { errors.description && <span className={ styles.error_message }>{ errors.description.message }</span> }
        </div>

        <div className={ styles.metrics_section }>
          <div className={ styles.metrics_header }>
            <div className={ styles.metrics_intro }>
              <span className={ styles.metrics_intro_title }>Select evaluation metrics</span>
              <span className={ styles.metrics_intro_help }>
                Don&apos;t see a metric? You may not have Owner access to use that metric in an evaluation.
              </span>
            </div>
            <Input
              id="metric-name-filter"
              type="text"
              label="Filter by metric name"
              placeholder="Filter by metric name..."
              value={ metricNameFilter }
              onChange={ ( e ) => {
                setMetricNameFilter( e.target.value );
                resetCursor();
              } }
              disabled={ isPending }
            />
          </div>

          { selectedMetrics.size > 0 && (
            <span className={ styles.selected_count } data-testid="selected-metrics-count">
              { selectedMetrics.size } metric{ selectedMetrics.size === 1 ? '' : 's' } selected
            </span>
          ) }

          { isLoading ? (
            <Loader />
          ) : isError ? (
            <div className={ styles.error_message }>Error loading metrics</div>
          ) : hasNoAccessibleMetrics ? (
            <div className={ styles.zero_state } data-testid="metrics-zero-state">
              <span className={ styles.zero_state_title }>
                You don&apos;t currently have Owner access to any metrics.
              </span>
              <span className={ styles.zero_state_body }>
                Create a new metric, or contact a metric owner to request access to an existing one.
              </span>
            </div>
          ) : (
            <MetricsTable
              metrics={ displayedMetrics }
              selectedMetrics={ selectedMetrics }
              selectedVersions={ selectedVersions }
              isPending={ isPending }
              cursorPagination={ {
                hasMore: data?.hasMore ?? false,
                canGoPrev,
                currentPage,
                pageSize,
                onNextPage: handleNextPage,
                onPrevPage: goPrev,
                onPageSizeChange: handlePageSizeChange
              } }
              onMetricToggle={ onMetricToggle }
              onMetricVersionChange={ onMetricVersionChange }
            />
          ) }
        </div>

        <div className={ styles.footer }>
          { !canSubmit && (
            <span className={ styles.footer_hint }>
              Complete required fields to create an evaluation.
            </span>
          ) }
          <Button variant="secondary" size="sm" type="button" onClick={ onClose } disabled={ isPending }>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="submit"
            loading={ isPending }
            disabled={ !canSubmit }
            data-testid="create-evaluation-submit-button"
          >
            Create Evaluation
          </Button>
        </div>
      </form>
    </Modal>
  );
}
